import { BehaviorSubject } from 'rxjs';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { GridColumnVisibilityModel, GridFilterModel, GridLogicOperator, GridSortModel } from '@mui/x-data-grid-pro';
import { addError, clearError } from './Error';
import { getUserCustomizations, updateUserCustomizationData } from '../Api/Customizations/UserCustomizationsApi';
import { DefaultCustomerGridColumnDefs } from '../components/Pages/Customer/CustomerGridColumnDefs';
import { DefaultAssetGridColumnDefs } from '../components/Pages/Asset/AssetGridColumnDefs';
import { DefaultBncAssetGridColumnDefs } from '../components/Pages/Asset/BncAssetGridColumnDefs';
import { DefaultProvisionalAssetGridColumnDefs } from '../components/Pages/ProvisionalAsset/ProvisionalAssetGridColumnDefs';
import { DefaultLinkEventGridColumnDefs } from '../components/Pages/Admin/ColumnDefs/LinkEventGridColumnDefs';
import { GridCustomColDef } from '../components/types';
import { DefaultRPMGridColumnDefs } from '../components/Pages/RPM/RPMGridColumnDefs';
import { DefaultApMapGridColumnDefs } from '../components/Pages/Data/ColumnDefs/ApMapGridColumnDefs';
import { DefaultInvalidLinkGridColumnDefs } from '../components/Pages/Data/ColumnDefs/InvalidLinkGridColumnDefs';
import { DefaultTsdMapGridColumnDefs } from '../components/Pages/Data/ColumnDefs//TsdMapGridColumnDefs';
import { DefaultSupplierMapGridColumnDefs } from '../components/Pages/Data/ColumnDefs/SupplierMapGridColumnDefs';
import { DefaultCommissionGridColumnDefs } from '../components/Pages/Data/ColumnDefs/CommissionGridColumnDefs';
import { DefaultTicketGridColumnDefs } from '../components/Pages/Ticket/TicketGridColumnDefs';
import { DefaultUserGridColumnDefs } from '../components/Pages/Admin/ColumnDefs/UserGridColumnDefs';
import { DefaultTFUGridColumnDefs } from '../components/Pages/TFU/TFUGridColumnDefs';
import { DefaultLinkGridColumnDefs } from '../components/Pages/Link/LinkGridColumnDefs';
import { getTabs } from '../utils/helpers';
import { DefaultProductMapGridColumnDefs } from '../components/Pages/Data/ColumnDefs/ProductMapGridColumnDefs';
import { DefaultUnmappedProductGridColumnDefs } from '../components/Pages/Data/ColumnDefs/UnmappedProductGridColumnDefs';
import { DefaultCustomerMapGridColumnDefs } from '../components/Pages/Data/ColumnDefs/CustomerMapGridColumnDefs';
import { DefaultUnmappedCustomerGridColumnDefs } from '../components/Pages/Data/ColumnDefs/UnmappedCustomerGridColumnDefs';
import { DefaultRunTotalAuditGridColumnDefs } from '../components/Pages/Admin/Tabs/RunTotalAudit/RunTotalAuditGridColumnDefs';
import { DefaultPaymentGridColumnDefs } from '../components/Pages/Payment/PaymentGridColumnDefs';
import { DefaultCorrectionGridColumnDefs } from '../components/Pages/Correction/CorrectionGridColumnDefs';
import { DefaultAdjustmentGridColumnDefs } from '../components/Pages/Adjustment/AdjustmentGridColumnDefs';
import { DefaultHistoryGridColumnDefs } from '../components/Pages/History/HistoryGridColumnDefs';
import { DefaultAttributionGridColumnDefs } from '../components/Pages/Attribution/DirectSeller/GridColumnDefs';
import { DefaultAssetRevenueAttributionGridColumnDefs } from '../components/Pages/Attribution/AssetRevenue/GridColumnDefs';
import { DefaultRunGridColumnDefs } from '../components/Pages/Run/RunGridColumnDefs';
import { PaymentRecipientType } from './PaymentRecipient';

export interface GridPreferences {
  muiConfig: GridInitialState;
  disabledFields: string[];
  customView?: boolean;
  id?: string;
}

type AssetGridKeys = {
  [key in `assetGrid_${string}`]?: GridPreferences;
};

type LinkGridKeys = {
  [key in `linkGrid_${string}`]?: GridPreferences;
};

type RPMGridKeys = {
  [key in `RPMGrid_${string}`]?: GridPreferences;
};

type CustomerGridKeys = {
  [key in `customerGrid_${string}`]?: GridPreferences;
};

type PaymentGridKeys = {
  [key in `paymentGrid_${string}`]?: GridPreferences;
};

type CorrectionGridKeys = {
  [key in `correctionGrid_${string}`]?: GridPreferences;
};

type AdjustmentGridKeys = {
  [key in `adjustmentGrid_${string}`]?: GridPreferences;
};

type HistoryGridKeys = {
  [key in `historyGrid_${string}`]?: GridPreferences;
};

type AttributionGridKeys = {
  [key in `attributionGrid_${string}`]?: GridPreferences;
};

export type TicketGridKeys = {
  [key in `ticketGrid_${string}`]?: GridPreferences;
};

export type TFUGridKeys = {
  [key in `tfuGrid_${string}`]?: GridPreferences;
};

export type ProvisionalAssetGridKeys = {
  [key in `provisionalAssetGrid_${string}`]?: GridPreferences;
};

export interface UserPreferencesContent
  extends
    AssetGridKeys,
    LinkGridKeys,
    RPMGridKeys,
    CustomerGridKeys,
    TicketGridKeys,
    TFUGridKeys,
    ProvisionalAssetGridKeys,
    PaymentGridKeys,
    CorrectionGridKeys,
    AdjustmentGridKeys,
    AttributionGridKeys,
    HistoryGridKeys {
  userGrid: GridPreferences;
  commissionGrid: GridPreferences;
  customerGrid_All: GridPreferences;
  customerGrid_BnC: GridPreferences;
  customerGrid_Commissioning: GridPreferences;
  assetGrid_All: GridPreferences;
  assetGrid_Mine: GridPreferences;
  assetGrid_MyOpenBnC: GridPreferences;
  assetGrid_OpenBnC: GridPreferences;
  assetGrid_BnC: GridPreferences;
  assetGrid_Commissioning: GridPreferences;
  assetGrid_MissingLink: GridPreferences;
  assetGrid_MissingRun: GridPreferences;
  assetGrid_DisconnectedNotDecommissioned: GridPreferences;
  assetGrid_ValidationNeeded: GridPreferences;
  assetGrid_OriginalStillCommissioning: GridPreferences;
  assetGrid_FirstRunBeforeSign: GridPreferences;
  assetGrid_OriginalandRenewalOverlap: GridPreferences;
  linkGrid_Active: GridPreferences;
  linkGrid_Candidate: GridPreferences;
  linkGrid_Archived: GridPreferences;
  provisionalAssetGrid_All: GridPreferences;
  provisionalAssetGrid_Mine: GridPreferences;
  provisionalAssetGrid_Commissioning: GridPreferences;
  provisionalAssetGrid_MissingLink: GridPreferences;
  provisionalAssetGrid_ValidationNeeded: GridPreferences;
  provisionalAssetGrid_Archived: GridPreferences;
  provisionalAssetGrid_BnC: GridPreferences;
  linkEventGrid: GridPreferences;
  apMapGrid: GridPreferences;
  runTotalAuditGrid: GridPreferences;
  ticketGrid_MyOpen: GridPreferences;
  ticketGrid_MyClosed: GridPreferences;
  ticketGrid_Open: GridPreferences;
  ticketGrid_Closed: GridPreferences;
  ticketGrid_MyOverdue: GridPreferences;
  ticketGrid_Overdue: GridPreferences;
  ticketGrid_Recent: GridPreferences;
  ticketGrid_All: GridPreferences;
  invalidLinkGrid: GridPreferences;
  tsdMapGrid: GridPreferences;
  supplierMapGrid: GridPreferences;
  RPMGrid_All: GridPreferences;
  RPMGrid_NewUnlinked: GridPreferences;
  RPMGrid_AllUnlinked: GridPreferences;
  tfuGrid_All: GridPreferences;
  tfuGrid_Overdue: GridPreferences;
  tfuGrid_Pending: GridPreferences;
  tfuGrid_MyOverdue: GridPreferences;
  tfuGrid_MyPending: GridPreferences;
  productMapGrid: GridPreferences;
  unmappedProductGrid: GridPreferences;
  customerMapGrid: GridPreferences;
  unmappedCustomerGrid: GridPreferences;
  calculatedRunGrid: GridPreferences;
  datePastRunGrid: GridPreferences;
  dateFutureRunGrid: GridPreferences;
  paymentGrid_All: GridPreferences;
  correctionGrid_All: GridPreferences;
  correctionGrid_ActiveRun: GridPreferences;
  adjustmentGrid_ActiveRunTagged: GridPreferences;
  adjustmentGrid_ActiveRunUntagged: GridPreferences;
  adjustmentGrid_AllTagged: GridPreferences;
  adjustmentGrid_AllUntagged: GridPreferences;
  adjustmentGrid_Ineligible: GridPreferences;
  attributionGrid_Summary: GridPreferences;
  attributionGrid_Detail: GridPreferences;
  assetRevenueAttributionGrid: GridPreferences;
  historyGrid_Combined: GridPreferences;
  historyGrid_Assets: GridPreferences;
  historyGrid_ProvisionalAssets: GridPreferences;
  historyGrid_Tickets: GridPreferences;
}

export interface UserCustomizations {
  content: UserPreferencesContent;
  id?: string;
  userId?: number;
}

export type PrefKey = keyof UserPreferencesContent;

export const gridInitialState = (
  cols: GridCustomColDef[],
  sortModel?: GridSortModel,
  filterModel?: GridFilterModel
): GridInitialState => {
  const visibility: GridColumnVisibilityModel = {};
  const pinned: { left: string[]; right: string[] } = { left: [], right: [] };
  if (cols) {
    cols.forEach(({ field, hideByDefault, pinned: pinnedLocation }: GridCustomColDef) => {
      visibility[field] = hideByDefault != true;
      if (pinnedLocation === 'left') {
        pinned.left.push(field);
      } else if (pinnedLocation === 'right') {
        pinned.right.push(field);
      }
    });
  }

  return {
    pinnedColumns: pinned,
    columns: {
      columnVisibilityModel: visibility,
      orderedFields: cols ? cols.map(({ field }) => field) : [],
      dimensions: {}
    },
    preferencePanel: {
      open: false
    },
    filter: {
      filterModel: filterModel || {
        items: [],
        logicOperator: 'and' as GridLogicOperator,
        quickFilterValues: [],
        quickFilterLogicOperator: 'and' as GridLogicOperator
      }
    },
    sorting: {
      sortModel: sortModel || []
    },
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100
      }
    }
  };
};

export const defaultSort: { [key: string]: GridSortModel } = {
  customerGrid_All: [{ field: 'name', sort: 'asc' }],
  customerGrid_BnC: [{ field: 'name', sort: 'asc' }],
  customerGrid_Commissioning: [{ field: 'name', sort: 'asc' }],
  assetGrid_All: [{ field: 'number', sort: 'desc' }],
  assetGrid_Mine: [{ field: 'number', sort: 'desc' }],
  assetGrid_MyOpenBnC: [{ field: 'number', sort: 'desc' }],
  assetGrid_OpenBnC: [{ field: 'number', sort: 'desc' }],
  assetGrid_BnC: [{ field: 'number', sort: 'desc' }],
  assetGrid_Commissioning: [{ field: 'number', sort: 'desc' }],
  assetGrid_MissingLink: [{ field: 'number', sort: 'desc' }],
  assetGrid_MissingRun: [{ field: 'number', sort: 'desc' }],
  assetGrid_DisconnectedNotDecommissioned: [{ field: 'number', sort: 'desc' }],
  assetGrid_ValidationNeeded: [{ field: 'number', sort: 'desc' }],
  assetGrid_OriginalStillCommissioning: [{ field: 'number', sort: 'desc' }],
  assetGrid_FirstRunBeforeSign: [{ field: 'number', sort: 'desc' }],
  assetGrid_OriginalandRenewalOverlap: [{ field: 'number', sort: 'desc' }],
  linkGrid_Active: [{ field: 'rpmAccountId', sort: 'desc' }],
  linkGrid_Candidate: [{ field: 'rpmAccountId', sort: 'desc' }],
  linkGrid_Archived: [{ field: 'rpmAccountId', sort: 'desc' }],
  runTotalAuditGrid: [{ field: 'createdAt', sort: 'desc' }],
  provisionalAssetGrid_All: [{ field: 'assetNumber', sort: 'desc' }],
  provisionalAssetGrid_Mine: [{ field: 'assetNumber', sort: 'desc' }],
  provisionalAssetGrid_Commissioning: [{ field: 'assetNumber', sort: 'desc' }],
  provisionalAssetGrid_MissingLink: [{ field: 'assetNumber', sort: 'desc' }],
  provisionalAssetGrid_ValidationNeeded: [{ field: 'assetNumber', sort: 'desc' }],
  provisionalAssetGrid_Archived: [{ field: 'assetNumber', sort: 'desc' }],
  provisionalAssetGrid_BnC: [{ field: 'assetNumber', sort: 'desc' }],
  ticketGrid_MyOpen: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_MyClosed: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_Open: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_Closed: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_MyOverdue: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_Overdue: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_Recent: [{ field: 'updatedAt', sort: 'desc' }],
  ticketGrid_All: [{ field: 'updatedAt', sort: 'desc' }],
  commissionGrid: [{ field: 'createdAt', sort: 'desc' }],
  linkEventGrid: [{ field: 'linkAction.createdAt', sort: 'desc' }],
  RPMGrid_All: [{ field: 'accountId', sort: 'asc' }],
  RPMGrid_NewUnlinked: [{ field: 'accountId', sort: 'asc' }],
  RPMGrid_AllUnlinked: [{ field: 'accountId', sort: 'asc' }],
  tfuGrid_All: [{ field: 'dueDate', sort: 'asc' }],
  tfuGrid_Overdue: [{ field: 'dueDate', sort: 'asc' }],
  tfuGrid_Pending: [{ field: 'dueDate', sort: 'asc' }],
  tfuGrid_MyOverdue: [{ field: 'dueDate', sort: 'asc' }],
  tfuGrid_MyPending: [{ field: 'dueDate', sort: 'asc' }],
  paymentGrid_All: [{ field: 'date', sort: 'desc' }],
  correctionGrid_All: [{ field: 'paidInRunDate', sort: 'desc' }],
  correctionGrid_ActiveRun: [{ field: 'paidInRunDate', sort: 'desc' }],
  adjustmentGrid_ActiveRunTagged: [{ field: 'runDate', sort: 'desc' }],
  adjustmentGrid_ActiveRunUntagged: [{ field: 'runDate', sort: 'desc' }],
  adjustmentGrid_AllTagged: [{ field: 'runDate', sort: 'desc' }],
  adjustmentGrid_AllUntagged: [{ field: 'runDate', sort: 'desc' }],
  adjustmentGrid_Ineligible: [{ field: 'runDate', sort: 'desc' }],
  attributionGrid_Summary: [{ field: 'directSellerName', sort: 'asc' }],
  attributionGrid_Detail: [{ field: 'directSellerName', sort: 'asc' }],
  assetRevenueAttributionGrid: [{ field: 'asset_upstack_id', sort: 'asc' }],
  historyGrid_Combined: [{ field: 'changedAt', sort: 'desc' }],
  historyGrid_Assets: [{ field: 'changedAt', sort: 'desc' }],
  historyGrid_ProvisionalAssets: [{ field: 'changedAt', sort: 'desc' }],
  historyGrid_Tickets: [{ field: 'changedAt', sort: 'desc' }]
};

const assetsValidationParams = { showHiddenFields: ['lastCommissionRun'] };

const _defaultUserPreferences: UserCustomizations = {
  content: {
    userGrid: {
      muiConfig: gridInitialState(DefaultUserGridColumnDefs),
      disabledFields: []
    },
    commissionGrid: {
      muiConfig: gridInitialState(DefaultCommissionGridColumnDefs, defaultSort.commissionGrid),
      disabledFields: []
    },
    customerGrid_All: {
      muiConfig: gridInitialState(DefaultCustomerGridColumnDefs(), defaultSort.customerGrid_All),
      disabledFields: []
    },
    customerGrid_BnC: {
      muiConfig: gridInitialState(DefaultCustomerGridColumnDefs(), defaultSort.customerGrid_BnC),
      disabledFields: []
    },
    customerGrid_Commissioning: {
      muiConfig: gridInitialState(DefaultCustomerGridColumnDefs(), defaultSort.customerGrid_Commissioning),
      disabledFields: []
    },
    assetGrid_All: {
      muiConfig: gridInitialState(DefaultAssetGridColumnDefs(), defaultSort.assetGrid_All),
      disabledFields: []
    },
    assetGrid_Mine: {
      muiConfig: gridInitialState(DefaultAssetGridColumnDefs(), defaultSort.assetGrid_Mine),
      disabledFields: []
    },
    assetGrid_MyOpenBnC: {
      muiConfig: gridInitialState(DefaultBncAssetGridColumnDefs, defaultSort.assetGrid_MyOpenBnC),
      disabledFields: []
    },
    assetGrid_OpenBnC: {
      muiConfig: gridInitialState(DefaultBncAssetGridColumnDefs, defaultSort.assetGrid_OpenBnC),
      disabledFields: []
    },
    assetGrid_BnC: {
      muiConfig: gridInitialState(DefaultBncAssetGridColumnDefs, defaultSort.assetGrid_BnC),
      disabledFields: []
    },
    assetGrid_Commissioning: {
      muiConfig: gridInitialState(DefaultAssetGridColumnDefs(), defaultSort.assetGrid_Commissioning),
      disabledFields: []
    },
    assetGrid_MissingLink: {
      muiConfig: gridInitialState(
        DefaultAssetGridColumnDefs(assetsValidationParams),
        defaultSort.assetGrid_MissingLink
      ),
      disabledFields: []
    },
    assetGrid_MissingRun: {
      muiConfig: gridInitialState(DefaultAssetGridColumnDefs(assetsValidationParams), defaultSort.assetGrid_MissingRun),
      disabledFields: []
    },
    assetGrid_DisconnectedNotDecommissioned: {
      muiConfig: gridInitialState(
        DefaultAssetGridColumnDefs(assetsValidationParams),
        defaultSort.assetGrid_DisconnectedNotDecommissioned
      ),
      disabledFields: []
    },
    assetGrid_ValidationNeeded: {
      muiConfig: gridInitialState(
        DefaultAssetGridColumnDefs(assetsValidationParams),
        defaultSort.assetGrid_ValidationNeeded
      ),
      disabledFields: []
    },
    assetGrid_OriginalStillCommissioning: {
      muiConfig: gridInitialState(
        DefaultAssetGridColumnDefs(assetsValidationParams),
        defaultSort.assetGrid_OriginalStillCommissioning
      ),
      disabledFields: []
    },
    assetGrid_FirstRunBeforeSign: {
      muiConfig: gridInitialState(
        DefaultAssetGridColumnDefs(assetsValidationParams),
        defaultSort.assetGrid_FirstRunBeforeSign
      ),
      disabledFields: []
    },
    assetGrid_OriginalandRenewalOverlap: {
      muiConfig: gridInitialState(
        DefaultAssetGridColumnDefs(assetsValidationParams),
        defaultSort.assetGrid_OriginalandRenewalOverlap
      ),
      disabledFields: []
    },
    linkGrid_Active: {
      muiConfig: gridInitialState(DefaultLinkGridColumnDefs(), defaultSort.linkGrid_Active),
      disabledFields: []
    },
    linkGrid_Candidate: {
      muiConfig: gridInitialState(DefaultLinkGridColumnDefs(), defaultSort.linkGrid_Candidate),
      disabledFields: []
    },
    linkGrid_Archived: {
      muiConfig: gridInitialState(DefaultLinkGridColumnDefs(), defaultSort.linkGrid_Archived),
      disabledFields: []
    },
    provisionalAssetGrid_All: {
      muiConfig: gridInitialState(DefaultProvisionalAssetGridColumnDefs(), defaultSort.provisionalAssetGrid_All),
      disabledFields: []
    },
    provisionalAssetGrid_Mine: {
      muiConfig: gridInitialState(DefaultProvisionalAssetGridColumnDefs(), defaultSort.provisionalAssetGrid_Mine),
      disabledFields: []
    },
    provisionalAssetGrid_Commissioning: {
      muiConfig: gridInitialState(
        DefaultProvisionalAssetGridColumnDefs(),
        defaultSort.provisionalAssetGrid_Commissioning
      ),
      disabledFields: []
    },
    provisionalAssetGrid_BnC: {
      muiConfig: gridInitialState(DefaultProvisionalAssetGridColumnDefs(), defaultSort.provisionalAssetGrid_BnC),
      disabledFields: []
    },
    provisionalAssetGrid_MissingLink: {
      muiConfig: gridInitialState(
        DefaultProvisionalAssetGridColumnDefs(),
        defaultSort.provisionalAssetGrid_MissingLink
      ),
      disabledFields: []
    },
    provisionalAssetGrid_ValidationNeeded: {
      muiConfig: gridInitialState(
        DefaultProvisionalAssetGridColumnDefs(),
        defaultSort.provisionalAssetGrid_ValidationNeeded
      ),
      disabledFields: []
    },
    provisionalAssetGrid_Archived: {
      muiConfig: gridInitialState(DefaultProvisionalAssetGridColumnDefs(), defaultSort.provisionalAssetGrid_Archived),
      disabledFields: []
    },
    ticketGrid_MyOpen: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_MyOpen),
      disabledFields: []
    },
    ticketGrid_MyClosed: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_MyClosed),
      disabledFields: []
    },
    ticketGrid_Open: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_Open),
      disabledFields: []
    },
    ticketGrid_Closed: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_Closed),
      disabledFields: []
    },
    ticketGrid_MyOverdue: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_MyOpen),
      disabledFields: []
    },
    ticketGrid_Overdue: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_Overdue),
      disabledFields: []
    },
    ticketGrid_Recent: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_Recent),
      disabledFields: []
    },
    ticketGrid_All: {
      muiConfig: gridInitialState(DefaultTicketGridColumnDefs, defaultSort.ticketGrid_All),
      disabledFields: []
    },
    linkEventGrid: {
      muiConfig: gridInitialState(DefaultLinkEventGridColumnDefs, defaultSort.linkEventGrid),
      disabledFields: []
    },
    apMapGrid: {
      muiConfig: gridInitialState(DefaultApMapGridColumnDefs),
      disabledFields: []
    },
    runTotalAuditGrid: {
      muiConfig: gridInitialState(DefaultRunTotalAuditGridColumnDefs, defaultSort.runTotalAuditGrid),
      disabledFields: []
    },
    invalidLinkGrid: {
      muiConfig: gridInitialState(DefaultInvalidLinkGridColumnDefs),
      disabledFields: []
    },
    tsdMapGrid: {
      muiConfig: gridInitialState(DefaultTsdMapGridColumnDefs),
      disabledFields: []
    },
    supplierMapGrid: {
      muiConfig: gridInitialState(DefaultSupplierMapGridColumnDefs) as GridInitialState,
      disabledFields: []
    },
    RPMGrid_NewUnlinked: {
      muiConfig: gridInitialState(DefaultRPMGridColumnDefs, defaultSort.RPMGrid_NewUnlinked),
      disabledFields: []
    },
    RPMGrid_AllUnlinked: {
      muiConfig: gridInitialState(DefaultRPMGridColumnDefs, defaultSort.RPMGrid_AllUnlinked),
      disabledFields: []
    },
    RPMGrid_All: {
      muiConfig: gridInitialState(DefaultRPMGridColumnDefs, defaultSort.RPMGrid_All),
      disabledFields: []
    },
    tfuGrid_All: {
      muiConfig: gridInitialState(DefaultTFUGridColumnDefs(), defaultSort.tfuGrid_All),
      disabledFields: []
    },
    tfuGrid_Overdue: {
      muiConfig: gridInitialState(DefaultTFUGridColumnDefs(), defaultSort.tfuGrid_Overdue),
      disabledFields: []
    },
    tfuGrid_Pending: {
      muiConfig: gridInitialState(DefaultTFUGridColumnDefs(), defaultSort.tfuGrid_Pending),
      disabledFields: []
    },
    tfuGrid_MyOverdue: {
      muiConfig: gridInitialState(DefaultTFUGridColumnDefs(), defaultSort.tfuGrid_MyOverdue),
      disabledFields: []
    },
    tfuGrid_MyPending: {
      muiConfig: gridInitialState(DefaultTFUGridColumnDefs(), defaultSort.tfuGrid_MyPending),
      disabledFields: []
    },
    productMapGrid: {
      muiConfig: gridInitialState(DefaultProductMapGridColumnDefs, []),
      disabledFields: []
    },
    unmappedProductGrid: {
      muiConfig: gridInitialState(DefaultUnmappedProductGridColumnDefs({}), []),
      disabledFields: []
    },
    customerMapGrid: {
      muiConfig: gridInitialState(DefaultCustomerMapGridColumnDefs, []),
      disabledFields: []
    },
    unmappedCustomerGrid: {
      muiConfig: gridInitialState(DefaultUnmappedCustomerGridColumnDefs({}), []),
      disabledFields: []
    },
    calculatedRunGrid: {
      muiConfig: gridInitialState(DefaultRunGridColumnDefs, []),
      disabledFields: []
    },
    datePastRunGrid: {
      muiConfig: gridInitialState(DefaultRunGridColumnDefs, []),
      disabledFields: []
    },
    dateFutureRunGrid: {
      muiConfig: gridInitialState(DefaultRunGridColumnDefs, []),
      disabledFields: []
    },
    paymentGrid_All: {
      muiConfig: gridInitialState(DefaultPaymentGridColumnDefs(), defaultSort.paymentGrid_All),
      disabledFields: []
    },
    ...Object.fromEntries(
      Object.values(PaymentRecipientType ?? {}).map((type) => [
        `paymentGrid_${type}`,
        {
          muiConfig: gridInitialState(DefaultPaymentGridColumnDefs(), defaultSort.paymentGrid_All),
          disabledFields: []
        }
      ])
    ),
    correctionGrid_All: {
      muiConfig: gridInitialState(DefaultCorrectionGridColumnDefs(), defaultSort.correctionGrid_All),
      disabledFields: []
    },
    correctionGrid_ActiveRun: {
      muiConfig: gridInitialState(DefaultCorrectionGridColumnDefs(), defaultSort.correctionGrid_ActiveRun),
      disabledFields: []
    },
    adjustmentGrid_ActiveRunTagged: {
      muiConfig: gridInitialState(DefaultAdjustmentGridColumnDefs(), defaultSort.adjustmentGrid_ActiveRunTagged),
      disabledFields: []
    },
    adjustmentGrid_ActiveRunUntagged: {
      muiConfig: gridInitialState(DefaultAdjustmentGridColumnDefs(), defaultSort.adjustmentGrid_ActiveRunUntagged),
      disabledFields: []
    },
    adjustmentGrid_AllTagged: {
      muiConfig: gridInitialState(DefaultAdjustmentGridColumnDefs(), defaultSort.adjustmentGrid_AllTagged),
      disabledFields: []
    },
    adjustmentGrid_AllUntagged: {
      muiConfig: gridInitialState(DefaultAdjustmentGridColumnDefs(), defaultSort.adjustmentGrid_AllUntagged),
      disabledFields: []
    },
    adjustmentGrid_Ineligible: {
      muiConfig: gridInitialState(DefaultAdjustmentGridColumnDefs(), defaultSort.adjustmentGrid_Ineligible),
      disabledFields: []
    },
    attributionGrid_Summary: {
      muiConfig: gridInitialState(DefaultAttributionGridColumnDefs(), defaultSort.attributionGrid_Summary),
      disabledFields: []
    },
    attributionGrid_Detail: {
      muiConfig: gridInitialState(DefaultAttributionGridColumnDefs(), defaultSort.attributionGrid_Detail),
      disabledFields: []
    },
    assetRevenueAttributionGrid: {
      muiConfig: gridInitialState(
        DefaultAssetRevenueAttributionGridColumnDefs(),
        defaultSort.assetRevenueAttributionGrid
      ),
      disabledFields: []
    },
    historyGrid_Combined: {
      muiConfig: gridInitialState(DefaultHistoryGridColumnDefs(), defaultSort.historyGrid_Combined),
      disabledFields: []
    },
    historyGrid_Assets: {
      muiConfig: gridInitialState(DefaultHistoryGridColumnDefs(), defaultSort.historyGrid_Assets),
      disabledFields: []
    },
    historyGrid_ProvisionalAssets: {
      muiConfig: gridInitialState(DefaultHistoryGridColumnDefs(), defaultSort.historyGrid_ProvisionalAssets),
      disabledFields: []
    },
    historyGrid_Tickets: {
      muiConfig: gridInitialState(DefaultHistoryGridColumnDefs(), defaultSort.historyGrid_Tickets),
      disabledFields: []
    }
  }
};
export const getDefaultPreferences = (): UserCustomizations => JSON.parse(JSON.stringify(_defaultUserPreferences));

export interface CustomView {
  page: string;
  name: string;
}

const customViewSubject = new BehaviorSubject<CustomView[]>([]);
const customizationSubject = new BehaviorSubject<UserCustomizations | undefined>(undefined);
const customizationLoadingSubject = new BehaviorSubject<boolean>(false);
// Separate from the data: pages gate render and fetch on having settings, and there is no second load.
const customizationLoadFailedSubject = new BehaviorSubject<boolean>(false);
export const getCustomizationSubject = (): BehaviorSubject<UserCustomizations | undefined> => customizationSubject;
export const getCustomizationLoadingSubject = (): BehaviorSubject<boolean> => customizationLoadingSubject;
export const getCustomizationLoadFailedSubject = (): BehaviorSubject<boolean> => customizationLoadFailedSubject;
export const getCustomViewSubject = (): BehaviorSubject<CustomView[]> => customViewSubject;
export const getCurrentSettings = (): UserCustomizations | undefined => customizationSubject.getValue();

export const SETTINGS_NOT_LOADED_MESSAGE = 'Your table settings could not be loaded. Reload the page and try again.';
// A 2xx with an empty body reports neither data nor an error, so callers need something to say about it.
export const UNCONFIRMED_WRITE_MESSAGE = 'the server did not confirm the change';

// Every write POSTs the whole document, so writing the subject after a failed load replaces it with defaults.
const writableCustomizations = (): UserCustomizations | undefined => {
  const loaded = customizationSubject.getValue();
  return loaded?.content && !customizationLoadFailedSubject.getValue() ? loaded : undefined;
};

export const fetchUserCustomization = async (): Promise<UserCustomizations> => {
  customizationLoadingSubject.next(true);
  const { data, error } = await getUserCustomizations();
  customizationLoadingSubject.next(false);
  customizationLoadFailedSubject.next(false);

  if (data) {
    clearError('user-customization');
    const mergedPreferences = {
      content: {
        ...getDefaultPreferences().content,
        ...data?.content
      },
      id: data.id
    };
    const customViews = [
      ...getTabs(data.content, 'assetGrid_', true),
      ...getTabs(data.content, 'RPMGrid_', true),
      ...getTabs(data.content, 'linkGrid_', true),
      ...getTabs(data.content, 'ticketGrid_', true),
      ...getTabs(data.content, 'provisionalAssetGrid_', true),
      ...getTabs(data.content, 'tfuGrid_', true),
      ...getTabs(data.content, 'customerGrid_', true),
      ...getTabs(data.content, 'paymentGrid_', true),
      ...getTabs(data.content, 'correctionGrid_', true),
      ...getTabs(data.content, 'adjustmentGrid_', true),
      ...getTabs(data.content, 'attributionGrid_', true),
      ...getTabs(data.content, 'historyGrid_', true)
    ];
    customViewSubject.next(customViews);
    customizationSubject.next(mergedPreferences);
    return mergedPreferences;
  }

  // Only a 404 means the user has never saved a view; anything else that returned no settings could be hiding some.
  if (error?.errorResponse?.response?.status === 404) {
    clearError('user-customization');
  } else {
    addError('user-customization', error?.message ?? SETTINGS_NOT_LOADED_MESSAGE);
    customizationLoadFailedSubject.next(true);
  }

  customizationSubject.next(getDefaultPreferences());

  return getDefaultPreferences();
};

export type SaveGridResult = { saved: true } | { saved: false; reason: 'not-loaded' | 'failed' };

export const saveGridPreferences = async (page: PrefKey, update: GridPreferences): Promise<SaveGridResult> => {
  const loaded = writableCustomizations();
  if (!loaded) return { saved: false, reason: 'not-loaded' };

  const payload: UserCustomizations = JSON.parse(JSON.stringify(loaded));
  (payload.content[page] as GridPreferences) = update;

  const { data } = await updateUserCustomizations(payload, page);
  if (!data) return { saved: false, reason: 'failed' };

  // Mirrored, not published: subscribers gate data fetches on this subject, so emitting refetches every grid.
  (loaded.content[page] as GridPreferences) = update;

  return { saved: true };
};

export const resetGridPreferences = async (page: PrefKey): Promise<SaveGridResult> => {
  const loaded = writableCustomizations();
  if (!loaded) return { saved: false, reason: 'not-loaded' };

  const payload = JSON.parse(JSON.stringify(loaded));
  delete payload.content[page];

  const { data } = await updateUserCustomizations(payload, page, true);

  return data ? { saved: true } : { saved: false, reason: 'failed' };
};

export const updateUserCustomizations = async (update: UserCustomizations, _type?: string, updateSubject = false) => {
  if (!writableCustomizations()) return { data: null, error: { message: SETTINGS_NOT_LOADED_MESSAGE } };

  const { data, error } = await updateUserCustomizationData(update);

  if (data) {
    clearError('user-customization');
    const customViews = [
      ...getTabs(data.content, 'assetGrid_', true),
      ...getTabs(data.content, 'RPMGrid_', true),
      ...getTabs(data.content, 'linkGrid_', true),
      ...getTabs(data.content, 'ticketGrid_', true),
      ...getTabs(data.content, 'provisionalAssetGrid_', true),
      ...getTabs(data.content, 'tfuGrid_', true),
      ...getTabs(data.content, 'customerGrid_', true),
      ...getTabs(data.content, 'paymentGrid_', true),
      ...getTabs(data.content, 'correctionGrid_', true),
      ...getTabs(data.content, 'adjustmentGrid_', true),
      ...getTabs(data.content, 'attributionGrid_', true),
      ...getTabs(data.content, 'historyGrid_', true)
    ];
    customViewSubject.next(customViews);
    if (updateSubject) {
      const mergedPreferences = {
        content: {
          ...getDefaultPreferences().content,
          ...data?.content
        },
        id: data.id
      };
      customizationSubject.next(mergedPreferences);
    }
  }
  if (error) addError('user-customization', error.message);
  return { data, error };
};
